import React, { useEffect } from "react";
import { Card, Typography, Badge, Divider, Space } from "antd";
import {
  CarOutlined,
  ClockCircleOutlined,
  EnvironmentOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import {
  BREAKDOWN_STATUS_COLORS,
} from "../utils/constants";
import breakdownService from "../services/breakdownService";
import "../assets/styles/breakdownCard.css";

const { Text, Title } = Typography;

const BreakdownCard = ({
  breakdown,
  hideStatus = false,
  clickable = true,
  readOnly = false,
  mechanicStatuses = [],
}) => {
  const getStatusName = (id) => {
    const match = mechanicStatuses.find(s => Number(s.id) === Number(id));
    return match?.name ?? null;
  };
  const navigate = useNavigate();

  // Format dates for display
  const dateForDisplay =
    breakdown.dateTimeOffset ||
    breakdown.createdDate ||
    breakdown.createdOn ||
    breakdown.createdAt;
  const formattedDate = breakdownService.formatDate(dateForDisplay);
  const elapsedTime = breakdownService.calculateElapsedTime(dateForDisplay);
  const mechanics = breakdown.mechanicsJson
    ? JSON.parse(breakdown.mechanicsJson)
    : [];

  // Get status text and color
  const mechanicStatus =
    breakdown.breakdownMechanicStatus !== undefined &&
    breakdown.breakdownMechanicStatus !== null
      ? breakdown.breakdownMechanicStatus
      : breakdown.breakdownMechanicStatusId !== undefined
      ? breakdown.breakdownMechanicStatusId
      : null;
  const statusText =
    mechanicStatus !== null && mechanicStatus !== undefined
      ? getStatusName(mechanicStatus) || "Unknown"
      : breakdown.statusDescription ||
        breakdown.breakdownStatusDescription ||
        breakdown.breakdownStatus ||
        "Unknown";
  const statusColor =
    mechanicStatus !== null && mechanicStatus !== undefined
      ? BREAKDOWN_STATUS_COLORS[mechanicStatus] || "#999"
      : "#999";

  const breakdownTypeMap = {
    0: "Partial",
    1: "On Road",
    20: "Running Repair",
    30: "No Touch",
  };
  // Handle card click to navigate to details
  const handleCardClick = () => {
    const breakdownId = breakdown.id ?? breakdown.breakdownId;
    navigate(`/breakdown/${breakdownId}`);
  };

  // Respect read-only mode: disable click/hover when readOnly is true
  const isClickable = clickable && !readOnly;

  // Vehicle information display
  const vehicleInfo = breakdownService.getVehicleDisplayString(breakdown);

  return (
    <Card
      className="breakdown-card"
      onClick={isClickable ? handleCardClick : undefined}
      hoverable={isClickable}
    >
      <div className="breakdown-header">
        <div className="breakdown-reference">
          <Title level={5}>{breakdown.breakdownReference}</Title>
        </div>
        {!hideStatus && (
          <Badge
            className="status-badge"
            color={statusColor}
            text={statusText}
          />
        )}
      </div>

      <Divider className="card-divider" />

      <div className="breakdown-content">
        <div className="registration-number">
          <Space>
            <CarOutlined />
            <Text strong>{breakdown.registrationNumber}</Text>
          </Space>
        </div>

        {breakdown.vinNumber && (
          <Text className="vin-number">VIN: {breakdown.vinNumber}</Text>
        )}

        {vehicleInfo && <Text className="vehicle-info">{vehicleInfo}</Text>}

        <Text type="secondary" className="company-name">
          {breakdown.companyName}
        </Text>

        {breakdown.fleetNumber && (
          <Text type="secondary" className="fleet-number">
            Fleet: {breakdown.fleetNumber}
          </Text>
        )}

        <div className="breakdown-description">
          <Text strong>Issue: </Text>
          <Text>{breakdown.description || "No description provided"}</Text>
        </div>
        <div>
          <Space>
          <WarningOutlined />
          <Text>
            {breakdownTypeMap[breakdown.breakdownType] || "Unknown"}
          </Text>
          </Space>
        </div>

        {mechanics.length > 0 && (
          <div className="assigned-mechanics">
            <Text strong>Mechanics: </Text>
            <div className="mechanics-list">
              <Space size={6} wrap>
                {mechanics.map((m) => {
                  const status = m.MechanicStatus;
                  const color = BREAKDOWN_STATUS_COLORS[status] || "#999";
                  const statusLabel =
                    getStatusName(status) || "Unknown";
                  const key = m.MechanicId || m.MechanicName;
                  return (
                    <Badge
                      key={key}
                      color={color}
                      text={`${m.MechanicName} (${statusLabel})`}
                    />
                  );
                })}
              </Space>
            </div>
          </div>
        )}

        <div className="breakdown-footer">
          <Space>
            <ClockCircleOutlined />
            <Text type="secondary">{elapsedTime || formattedDate}</Text>
          </Space>

          {breakdown.breakdownAddress && (
            <div className="breakdown-address">
              <Space>
                <EnvironmentOutlined />
                <Text type="secondary" ellipsis className="address-text">
                  {breakdown.breakdownAddress}
                </Text>
              </Space>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
};

export default BreakdownCard;
